<script setup>
import { computed } from "vue";
import RatingReady from "@/Components/RatingReady.vue";
import ShareButtons from "@/Components/ShareButtons.vue";
import VerifiedIcon from "@/Components/VerifiedIcon.vue";
import PerkRow from "@/Components/PerkRow.vue";

/**
 * Карточка оффера — ставка на ДОВЕРИЕ.
 *
 * Что было: 40% площади занимало фото услуги (часто просто кабинет или руки с
 * бумагами), имя юриста не показывалось вовсе, а самый сильный элемент — цитата
 * клиента — лежала мелким серым текстом внизу. Десять карточек выглядели
 * одинаково, и глаз по ним соскальзывал.
 *
 * Что стало: фото ужато до аватара, зато сразу видно, КТО исполнитель, а
 * освободившееся место отдано доказательствам — отзыв крупно и сразу под
 * названием, дела в практике по этой теме, стаж, посчитанная скорость ответа.
 * Карточки различаются между собой ровно тем, чем различаются юристы.
 */
const set = defineProps({
  offer: Object,
  city: Object,
  getlwr: String,
  auth: Object,
  review: { type: Object, default: null },
  cases: { type: Number, default: 0 },
  replyNote: { type: String, default: '' },   // «обычно отвечает за час» — считается по базе
  rate: { type: Object, default: null },      // {avg, count} — отзывы по ЭТОЙ услуге в ЭТОМ городе
  perks: { type: Array, default: () => [] },  // значки юриста
  // Что за страница вокруг: город, услуга, подуслуга. Нужно, чтобы не показывать
  // чипы, которые повторяют адрес страницы.
  context: { type: Object, default: () => ({}) },
  // { items: [пути к трём превью], total: сколько всего } или null
  diplomas: { type: Object, default: null },
  // Режим «пригласить» (виден только автору вопроса):
  inviteContext: { type: Object, default: null },
  invited: { type: Array, default: () => [] },     // id уже приглашённых юристов
  inviteCount: { type: Number, default: 0 },        // сколько всего приглашено (для лимита)
  inviteBusy: { type: Boolean, default: false },
  onlineIds: { type: Array, default: () => [] },    // id дежурных «сейчас на сайте»
  // Первая карточка списка — это и есть LCP-кадр страницы. Её картинка обязана
  // грузиться сразу и в высоком приоритете: lazy на ней откладывает самую
  // заметную отрисовку на сотни миллисекунд.
  priority: { type: Boolean, default: false },
});

const emit = defineEmits(['toggle-invite']);

const isOffer = computed(() => set.offer?.type == 'uslugi');

// id юриста этой карточки (и у оффера, и у карточки-юриста есть user_id)
const lawyerId = computed(() => set.offer?.user_id || null);
const isOnline = computed(() => !!lawyerId.value && set.onlineIds.includes(lawyerId.value));
// Пригласить можно только онлайн-дежурного и только автору вопроса
const showInvite = computed(() => !!set.inviteContext && isOnline.value);
const isInvited = computed(() => set.invited.includes(lawyerId.value));
const inviteLimit = computed(() => set.inviteContext?.max || 3);
const limitReached = computed(() => set.inviteCount >= inviteLimit.value);

// Здесь только ИМЯ маршрута и параметры, а сам вызов route() — в шаблоне.
// Глобальной route() нет ни в SSR-бандле, ни в браузере — она приходит из плагина
// ZiggyVue через globalProperties, то есть видна только шаблону. Вызов в computed
// роняет весь SSR-рендер: страница приходит с пустым #app и собирается только в браузере.
const link = computed(() => isOffer.value
  ? ['uslugi.canonical.url', [set.offer.cities.url, set.offer.main.url, set.offer.second.url, set.offer.clean_url]]
  : ['lawyer', [set.offer.url]]);

// Аватар юриста; если его нет — фото услуги, как было раньше.
const avatar = computed(() => {
  const a = set.offer?.lawyer_avatar || set.offer?.file_path;
  return a ? 'https://nedicom.ru/' + a : null;
});
const lawyerName = computed(() => set.offer?.lawyer_name || '');

// Число отзывов рядом с цитатой: она — образец, и человек должен видеть, из
// какого объёма этот образец взят. Одно и то же число дважды на карточке не
// пишем, поэтому у имени остаётся только оценка, а счётчик уезжает под отзыв.
// Если отзыва на карточке нет, счётчик возвращается к имени — иначе он пропал
// бы вовсе.
// Как подтверждена личность автора отзыва. Значок был только в каруселях на
// странице услуги — а решение человек принимает здесь, на карточке.
const VERIFIED_LABELS = {
  yandex_id: 'Яндекс ID',
  gosuslugi: 'Госуслуги',
  in_person: 'Личная встреча',
  vk_id: 'VK ID',
};
const verifiedLabel = computed(() => VERIFIED_LABELS[set.review?.verified_type] || '');

// Ссылка «Все N отзывов» есть не всегда: у карточки без посчитанного рейтинга
// её нет вовсе. От этого зависит, где показывать дату отзыва.
const showAllReviews = computed(() => reviewsCount.value > 1 && (isOffer.value || lawyerLink.value));

const allReviewsLabel = computed(() => {
  const n = reviewsCount.value;
  const t = n % 100, o = n % 10;
  const word = (t >= 11 && t <= 14) ? 'отзывов'
    : o === 1 ? 'отзыв' : (o >= 2 && o <= 4) ? 'отзыва' : 'отзывов';
  return 'Все ' + n + ' ' + word;
});

const diplomaItems = computed(() => set.diplomas?.items || []);
const diplomaLabel = computed(() => {
  const n = set.diplomas?.total || 0;
  // «Диплом» в единственном числе, дальше — счёт, чтобы не писать «1 дипломов».
  if (n <= 1) return 'Диплом и сертификаты';
  const t = n % 100, o = n % 10;
  const word = (t >= 11 && t <= 14) ? 'дипломов'
    : o === 1 ? 'диплом' : (o >= 2 && o <= 4) ? 'диплома' : 'дипломов';
  return n + ' ' + word + ' и сертификатов';
});

// Чип показываем, только если он отличается от того, что и так написано на странице.
const same = (a, b) => !!a && !!b && String(a).trim() === String(b).trim();

// ...и если он не пересказывает заголовок самого оффера. «Раздел имущества
// супругов» под заголовком «Юрист по разделу имущества» — это та же фраза в
// другом падеже. Точное сравнение тут не работает, поэтому сверяем основы слов:
// берём первые пять букв каждого слова длиннее четырёх и считаем совпадения.
// Порог 60% отсекает пересказ, но оставляет «Семейный юрист» — он про категорию,
// а не про эту услугу.
function stems(text) {
  return String(text || '').toLowerCase().replace(/ё/g, 'е')
    .split(/[^а-яa-z0-9]+/).filter(w => w.length > 4).map(w => w.slice(0, 5));
}
function echoesTitle(chip) {
  const c = stems(chip);
  if (!c.length) return false;
  const t = new Set(stems(set.offer?.usl_name));
  return c.filter(w => t.has(w)).length / c.length >= 0.6;
}
const showCity = computed(() => !!set.offer?.cities?.title && !same(set.offer.cities.title, set.context?.city));
const showMain = computed(() => !!set.offer?.main?.name
  && !same(set.offer.main.name, set.context?.main) && !echoesTitle(set.offer.main.name));
const showSecond = computed(() => {
  const n = set.offer?.second?.name;
  return !!n && n !== 'Общая категория' && !same(n, set.context?.second) && !echoesTitle(n);
});

const priceLabel = computed(() => {
  const p = isOffer.value ? Number(set.offer.price) : 1000;
  return p === 0 ? 'Бесплатно' : p + ' ₽';
});

// Стаж: берём из оффера, если задан (>1); иначе — реальный стаж из профиля юриста.
// «1» считаем дефолтом (не указан) и не показываем.
const expirience = computed(() => {
  const o = Number(set.offer?.expirience) || 0;
  const u = Number(set.offer?.user_expirience) || 0;
  const v = o > 1 ? o : u;
  return v > 1 ? v : 0;
});

function plural(n, one, few, many) {
  const a = Math.abs(n) % 100, b = a % 10;
  if (a > 10 && a < 20) return many;
  if (b > 1 && b < 5) return few;
  if (b === 1) return one;
  return many;
}
const yearsWord = computed(() => plural(expirience.value, 'год', 'года', 'лет'));
const casesWord = computed(() => plural(set.cases, 'дело', 'дела', 'дел'));

const categoryName = computed(() => {
  const sub = set.offer?.second?.name;
  const main = set.offer?.main?.name;
  // «Общая категория» (плейсхолдер-подуслуга) → показываем главную категорию
  if (sub && sub !== 'Общая категория') return sub;
  return main || sub || '';
});
// Дата отзыва: «май 2026». Число дня не показываем — в карточке важна
// свежесть, а не точный день, и короткая форма не ломает строку.
const reviewDate = computed(() => {
  const raw = set.review?.created_at;
  if (!raw) return '';
  const d = new Date(String(raw).replace(' ', 'T'));
  if (isNaN(d)) return '';
  return d.toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' }).replace(' г.', '');
});

// Рейтинг берём из посчитанного на сервере: только отзывы об услугах этого
// юриста в этой услуге и городе. Поля offer.final_rating / total_count больше
// не используем — они складывали весь пул юриста за все годы, да ещё и дважды.
const reviewsCount = computed(() => Number(set.rate?.count) || 0);
const ratingValue = computed(() => (Number(set.rate?.avg) || 0).toFixed(1));

// Ссылка на профиль юриста — отдельно от ссылки на объявление.
const lawyerLink = computed(() => (lawyerId.value ? ['lawyer', [lawyerId.value]] : null));
</script>

<template>
  <article
    class="relative my-2 md:my-3 rounded-xl border bg-white shadow-sm hover:shadow-md transition-shadow"
    :class="showInvite && isInvited ? 'border-indigo-400 ring-1 ring-indigo-300' : 'border-gray-100'"
    itemprop="offers"
    itemscope
    itemtype="https://schema.org/Offer"
  >
    <div class="p-3.5 md:p-4 flex flex-col min-w-0">

      <!-- ВЕРХНЯЯ ПОЛОСА: кто и что предлагает — слева, чужая речь — справа. -->
      <div class="lg:grid lg:grid-cols-[minmax(0,1fr)_300px] lg:gap-x-7 lg:items-start">

      <!-- КТО. Раньше исполнителя на карточке не было видно вовсе. -->
      <div class="flex items-start gap-3">
        <a :href="lawyerLink ? route(lawyerLink[0], lawyerLink[1]) : route(link[0], link[1])" class="shrink-0 relative block">
          <img
            v-if="avatar"
            :src="avatar"
            :alt="lawyerName || set.offer.usl_name"
            class="w-20 h-20 md:w-24 md:h-24 rounded-full object-cover object-top bg-gray-100"
            :loading="set.priority ? 'eager' : 'lazy'"
            :fetchpriority="set.priority ? 'high' : 'auto'"
            decoding="async"
            itemprop="image"
            onerror="this.style.visibility='hidden'"
          />
          <span
            v-if="isOnline"
            class="absolute bottom-0.5 right-0.5 block w-4 h-4 md:w-5 md:h-5 rounded-full bg-green-500 border-2 border-white"
            title="Сейчас на сайте"
          ></span>
        </a>

        <!-- Заголовок в одной колонке с именем: иначе он уходил под аватар на всю
             ширину, и аватар оставался висеть слева сам по себе. -->
        <div class="min-w-0 flex-1">
          <div class="flex items-start justify-between gap-2">
            <div class="min-w-0">
              <!-- Имя ведёт в ПРОФИЛЬ юриста, а не на объявление: и человек ждёт
                   именно этого, и страницы юристов получают ссылку с анкором-именем
                   с каждой страницы каталога. -->
              <a v-if="lawyerName && lawyerLink" :href="route(lawyerLink[0], lawyerLink[1])"
                class="block text-sm font-semibold text-gray-900 hover:text-indigo-700 transition-colors truncate">
                {{ lawyerName }}
              </a>
              <!-- Рейтинг стоит здесь, а не в правом верхнем углу карточки: угол
                   отдан отзыву, а оценка и так относится к юристу, чьё имя строкой
                   выше. Заодно опыт, оценка и «сейчас на сайте» читаются одной
                   фразой, а не тремя разбросанными по карточке значениями. -->
              <p class="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-gray-500">
                <span v-if="expirience">{{ expirience }} {{ yearsWord }} опыта</span>
                <span v-if="reviewsCount" class="flex items-center gap-1 whitespace-nowrap">
                  <svg class="w-3.5 h-3.5 text-yellow-300 shrink-0" fill="currentColor" viewBox="0 0 22 20" aria-hidden="true">
                    <use href="#ic-star" />
                  </svg>
                  <span class="font-semibold text-gray-900">{{ ratingValue }}</span>
                  <span v-if="!set.review">· {{ reviewsCount }}</span>
                </span>
                <span v-if="isOnline" class="text-green-700 font-medium">сейчас на сайте</span>
              </p>
            </div>

          </div>

          <!-- ЧТО -->
          <a
            :href="route(link[0], link[1])"
            itemprop="url"
            class="mt-2 block text-base md:text-lg font-bold leading-snug text-gray-900 hover:text-indigo-700 transition-colors line-clamp-2"
          ><span itemprop="name">{{ set.offer.usl_name }}</span></a>

          <!-- Чипы. Показываем ТОЛЬКО то, что отличается от страницы: на странице
               «Оспорить завещание · Симферополь» плашки «Симферополь» и «Оспорить
               завещание» повторяли её адрес на каждой из десяти карточек и не
               сообщали ничего. Оставшийся чип теперь значит «этот оффер из другой
               подуслуги» — то есть несёт смысл. -->
          <div v-if="isOffer && (showCity || showMain || showSecond)" class="mt-2 flex flex-wrap gap-1.5">
            <span v-if="showCity" class="inline-flex items-center gap-1 rounded-full bg-gray-100 text-gray-600 text-xs px-2.5 py-1">
              <svg class="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" />
              </svg>
              {{ set.offer.cities.title }}
            </span>
            <span v-if="showMain" class="inline-flex items-center rounded-full bg-indigo-50 text-indigo-700 text-xs px-2.5 py-1">
              {{ set.offer.main.name }}
            </span>
            <span v-if="showSecond" class="inline-flex items-center rounded-full bg-gray-100 text-gray-600 text-xs px-2.5 py-1">
              {{ set.offer.second.name }}
            </span>
          </div>


        </div>
      </div>

      <!-- ДОКАЗАТЕЛЬСТВО — чужая речь, а не системное сообщение.
           Плашка была лишней: сиреневая заливка, скругления и толстая полоса —
           три украшения на одну цитату, столько получают уведомления об ошибке.
           Хуже того, сиреневый на странице занят кнопкой «Подробнее», и главный
           аргумент спорил цветом с целевым действием. Осталась тонкая линейка,
           Кегль 12 и курсив: цитата не должна спорить размером с названием
           услуги, а наклон отделяет чужую речь от нашего текста — это её
           единственное отличие после того, как ушла плашка. Ниже 12 не
           опускаемся: Google считает такой текст нечитаемым на телефоне. -->
      <figure v-if="set.review" class="mt-3 lg:mt-0 border-l-2 border-gray-200 pl-4">
        <blockquote class="text-xs text-gray-600 leading-relaxed italic line-clamp-2 lg:line-clamp-3">«{{ set.review.description }}»</blockquote>
        <figcaption class="mt-2 flex items-center gap-2 min-w-0 text-xs text-gray-500">
          <RatingReady :rating="Number(set.review.rating)" :reviewRating="false" />
          <span class="truncate min-w-0">{{ set.review.fio }}</span>
          <span v-if="verifiedLabel" class="inline-flex items-center gap-1 shrink-0"
                :title="'Личность автора подтверждена: ' + verifiedLabel">
            <VerifiedIcon :type="set.review.verified_type" />
            <span class="hidden sm:inline text-gray-500">{{ verifiedLabel }}</span>
          </span>
        </figcaption>

        <!-- Объём, из которого взята цитата. Ссылка, а не просто цифра: число
             отзывов человек всё равно хочет проверить, и «54» без возможности
             посмотреть их — обещание без подтверждения.
             У оффера ведём на его собственные отзывы, а не в профиль: человек
             читает карточку услуги и ждёт отзывы об этой услуге. В профиль —
             только с карточки юриста, где услуги нет. -->
        <!-- Нижняя строка: ссылка на все отзывы и дата. Когда ссылки нет (у
             карточки без посчитанного рейтинга), дата не прижимается вправо —
             иначе висит в углу сама по себе. В строку автора её вернуть нельзя:
             там она делит место со звёздами и значком подтверждения и сжимает
             имя до «М…». -->
        <div v-if="showAllReviews || reviewDate" class="mt-1.5 flex items-baseline gap-3">
        <a v-if="showAllReviews"
           :href="isOffer
             ? route(link[0], link[1]) + '#reviews'
             : route(lawyerLink[0], lawyerLink[1]) + '#otzivi'"
           class="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-indigo-700 transition-colors">
          {{ allReviewsLabel }}
          <svg class="w-3 h-3 shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </a>
        <!-- Дата переехала со строки автора вниз. Там она делила место со
             звёздами, именем и значком подтверждения, и имя сжималось до «М…» —
             автор отзыва переставал быть человеком с именем. -->
        <span v-if="reviewDate" class="shrink-0 text-xs text-gray-500" :class="showAllReviews ? 'ml-auto' : ''">{{ reviewDate }}</span>
        </div>
      </figure>

      </div>

      <!-- Описание услуги убрано из карточки. Замер по странице «Оспорить завещание»:
           967 символов из 18 019, то есть 5,4% текста, и шесть текстов из десяти —
           один шаблон с переставленными словами («Сначала честно разберём…»).
           Поисковик такое обесценивает, а человек читает шесть одинаковых обещаний
           подряд и перестаёт верить всем сразу. На собственной странице оффера
           текст остался. itemprop="description" снят вместе с текстом: размечать
           невидимое содержимое правилами структурированных данных запрещено. -->

      <!-- ПОЛОСА ДОКАЗАТЕЛЬСТВ. Раньше документы, дела и значки шли тремя
           отдельными строками, каждая со своим отступом, и карточка выглядела
           как список из четырёх разных списков. Здесь всё, чем юрист доказывает
           свои слова, стоит одной строкой — и цена с кнопкой в её правом конце,
           то есть прямо после доказательств. -->
      <div class="mt-auto pt-3 border-t border-gray-100 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 lg:gap-6">
        <!-- Доказательства обёрнуты отдельно, чтобы они переносились ВНУТРИ своей
             половины. Без обёртки цена с кнопкой уезжали на вторую строку и под
             дипломами оставалась пустая полоса во всю ширину карточки. -->
        <div class="flex flex-wrap items-center gap-x-5 gap-y-2 min-w-0">
      <!-- ДИПЛОМЫ. 26×34 было мало: на таком размере скан не читается ни как
           текст, ни как предмет. Чтобы глаз опознал «документ с печатью», нужны
           различимые рамка, шапка и подпись внизу — это примерно 42 пикселя.
           Кадр вертикальный, потому что сканы лежат в поле 1200×700 с чёрными
           полями по бокам: object-cover вырезает центр, то есть сам документ. -->
      <a v-if="diplomaItems.length && lawyerLink"
         :href="route(lawyerLink[0], lawyerLink[1])"
         class="flex flex-wrap items-center gap-x-2.5 gap-y-1.5 min-w-0 group/dip"
         :title="'Дипломы и сертификаты' + (lawyerName ? ': ' + lawyerName : '')"
      >
        <span class="flex items-center gap-1">
          <!-- Больше трёх превью в 390 px не помещается: подпись выдавливалась
               за край и наезжала на картинки. На узком экране показываем три,
               с 640 px — все шесть. -->
          <img v-for="(src, i) in diplomaItems" :key="i"
               :src="src" width="42" height="56" loading="lazy" decoding="async"
               alt="Диплом или сертификат юриста"
               class="w-[42px] h-[56px] object-cover rounded-sm border border-gray-200 bg-white transition group-hover/dip:border-indigo-300"
               :class="i >= 3 ? 'hidden sm:block' : ''"
          />
        </span>
        <span class="text-xs leading-snug text-gray-600 group-hover/dip:text-indigo-700 whitespace-nowrap">
          {{ diplomaLabel }}
        </span>
      </a>

      <!-- ЦИФРЫ. Дела считаются по этому офферу, то есть именно по его теме. -->
      <div v-if="cases || set.replyNote" class="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-600">
        <span v-if="cases" class="inline-flex items-start gap-1.5">
          <svg class="w-3.5 h-3.5 shrink-0 mt-0.5 text-indigo-600" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <span><b class="font-semibold text-gray-900">{{ cases }}</b> {{ casesWord }} в практике<span v-if="categoryName">: {{ categoryName }}</span></span>
        </span>
        <span v-if="set.replyNote" class="inline-flex items-center gap-1.5">
          <svg class="w-3.5 h-3.5 shrink-0 text-indigo-600" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {{ set.replyNote }}
        </span>
      </div>

      <!-- ЗНАЧКИ приглушены до серого и оживают цветом при наведении.
           Аватар — портрет, значки — тоже портреты: один настоящий и четыре
           рисованных, все круглые, все одного размера, все в одной строке
           зрения. Полный цвет остался в профиле, где значок объясняется и где
           он единственная картинка на экране.
           Подписи на карточке сняты: название всё так же лежит в разметке для
           поисковика и озвучки и всплывает подсказкой по наведению. -->
      <div v-if="set.perks.length">
        <PerkRow :perks="set.perks" :size="24" muted />
      </div>

        </div>

        <div class="shrink-0 flex items-center justify-end gap-3 md:gap-4">
        <!-- Ссылки «сохранить/поделиться» собраны здесь, рядом с ценой и кнопкой:
             раньше они висели в правом верхнем углу отдельной группой. -->
        <ShareButtons v-if="isOffer" :bundle="set.offer" :auth="set.auth" class="mr-auto" />
        <p class="text-lg md:text-xl font-extrabold leading-none text-gray-900 whitespace-nowrap">
          {{ priceLabel }}
        </p>
        <div class="flex items-center gap-2">
          <!-- Кнопка «пригласить» (только в режиме приглашения, только у автора вопроса) -->
          <button
            v-if="showInvite"
            type="button"
            @click.stop.prevent="emit('toggle-invite', lawyerId)"
            :disabled="inviteBusy || (!isInvited && limitReached)"
            class="inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            :class="isInvited
              ? 'bg-indigo-600 text-white hover:bg-indigo-700'
              : 'bg-white text-indigo-700 border border-indigo-200 hover:bg-indigo-50'"
            :title="!isInvited && limitReached ? 'Можно пригласить не более ' + inviteLimit + ' юристов' : ''"
          >
            <svg v-if="isInvited" class="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
            </svg>
            <svg v-else class="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" />
            </svg>
            <span>{{ isInvited ? 'Приглашён' : 'Пригласить' }}</span>
          </button>

          <a
            :href="route(link[0], link[1])"
            class="inline-flex items-center gap-1.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium px-3.5 py-2 md:px-4 md:py-2.5 transition-colors whitespace-nowrap"
          >
            Подробнее
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
            </svg>
          </a>
        </div>
      </div>

      </div>

      <!-- Schema-разметка (скрыто).
           Доставка (OfferShippingDetails) и возврат (MerchantReturnPolicy) убраны:
           это свойства товара, который везут и который можно вернуть в магазин за
           30 дней. У консультации юриста нет ни доставки, ни возврата — разметка
           описывала то, чего не существует. -->
      <meta itemprop="price" :content="isOffer ? set.offer.price : 1000" />
      <meta itemprop="priceCurrency" content="RUB" />
      <meta itemprop="priceValidUntil" content="2026-12-31" />
      <link itemprop="availability" href="https://schema.org/InStock" />
      <meta v-if="set.offer.cities" itemprop="areaServed" :content="set.offer.cities.title" />
      <meta v-if="categoryName" itemprop="category" :content="categoryName" />

      <!-- Исполнитель. Раньше объявление висело само по себе, не связанное с
           человеком; теперь оно указывает на профиль, где лежит ProfilePage
           с Person, отзывами и рейтингом. -->
      <div v-if="lawyerName && lawyerLink" itemprop="seller" itemscope itemtype="https://schema.org/Person" class="hidden">
        <meta itemprop="name" :content="lawyerName" />
        <link itemprop="url" :href="route(lawyerLink[0], lawyerLink[1])" />
        <link v-if="avatar" itemprop="image" :href="avatar" />
      </div>
    </div>
  </article>
</template>
